import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import { uploadImage, uploadVideo, waitForStreamReady } from '../utils/cloudflare';
import { MediaTile, MediaTypeBadge, MediaLightbox, isPlayable } from './MediaGallery';
import { useTranslation } from '../LanguageContext';

/**
 * The creator's own gallery: drop zone, upload queue and grid.
 *
 * The old panel was a spinner and nothing else — a 300 MB video and a stalled
 * connection looked exactly alike, and the only way to find out which one you
 * had was to wait. This one reports the same three phases a video upload
 * actually has: the bytes going up (real percentage, straight off the XHR),
 * Cloudflare encoding them (polled, because until it finishes every thumbnail
 * 404s), and the row being saved.
 *
 * Files are checked *before* a byte leaves the machine, against the limits
 * that already exist on the other side: Cloudflare Images refuses over 10 MB,
 * and the Stream upload URL the backend mints is capped at 10 minutes.
 */

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_VIDEO_SECONDS = 600;

let seq = 0;
const nextKey = () => `up_${Date.now()}_${seq++}`;

/**
 * Duration/dimensions without uploading anything.
 *
 * The probe is advisory — it only exists to refuse a too-long video early — so
 * it gives up rather than blocking the queue. A container the browser cannot
 * read metadata from fires neither `loadedmetadata` nor `error` in some
 * browsers, and this runs *before* the file gets a tile: without the deadline
 * the picker would simply appear to do nothing at all.
 */
const PROBE_TIMEOUT_MS = 8000;

function probeVideo(file) {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        const src = URL.createObjectURL(file);
        let settled = false;
        const done = (value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            video.src = '';
            URL.revokeObjectURL(src);
            resolve(value);
        };
        const timer = setTimeout(() => done(null), PROBE_TIMEOUT_MS);
        video.preload = 'metadata';
        video.onloadedmetadata = () => done({ duration: video.duration });
        video.onerror = () => done(null);
        video.src = src;
    });
}

function ProgressRing({ percent }) {
    const radius = 22;
    const circumference = 2 * Math.PI * radius;
    return (
        <svg viewBox="0 0 56 56" className="w-14 h-14 -rotate-90">
            <circle cx="28" cy="28" r={radius} fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="4" />
            <circle
                cx="28" cy="28" r={radius} fill="none" stroke="currentColor" strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={circumference * (1 - Math.max(percent, 0) / 100)}
                style={{ transition: 'stroke-dashoffset 0.25s linear' }}
            />
        </svg>
    );
}

/** A file on its way up: local preview underneath, phase and progress on top. */
function QueueTile({ job, onCancel, onDismiss }) {
    const { t } = useTranslation();
    const failed = job.phase === 'error';

    const phaseLabel = {
        queued: t('gallery.queued'),
        uploading: t('gallery.uploading'),
        processing: t('gallery.processing'),
        saving: t('gallery.saving'),
        error: t('gallery.failed'),
    }[job.phase];

    return (
        <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-slate-900 ring-1 ring-slate-900/5 shadow-sm">
            {job.preview && (job.type === 'video' ? (
                <video src={job.preview} muted playsInline preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover opacity-60" />
            ) : (
                <img src={job.preview} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
            ))}
            <div className="absolute inset-0 bg-slate-950/55 backdrop-blur-[2px]" />

            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-3 text-center">
                {failed ? (
                    <div className="w-12 h-12 rounded-full bg-red-500/20 ring-1 ring-red-400/40 flex items-center justify-center">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5 text-red-300">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.01M10.34 3.94l-8.1 14.02A1.5 1.5 0 003.54 20.2h16.92a1.5 1.5 0 001.3-2.24l-8.1-14.02a1.5 1.5 0 00-2.6 0z" />
                        </svg>
                    </div>
                ) : (
                    <div className="relative text-primary-400">
                        <ProgressRing percent={job.phase === 'uploading' ? job.progress : 100} />
                        <span className="absolute inset-0 flex items-center justify-center text-[11px] font-black text-white tabular-nums">
                            {job.phase === 'uploading' ? `${job.progress}%` : ''}
                        </span>
                        {job.phase !== 'uploading' && (
                            <span className="absolute inset-0 flex items-center justify-center">
                                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            </span>
                        )}
                    </div>
                )}

                <p className="text-[11px] font-bold text-white/90">{phaseLabel}</p>
                {failed && job.error && (
                    <p className="text-[10px] text-red-200/90 leading-snug line-clamp-3">{job.error}</p>
                )}
                {!failed && job.phase === 'processing' && (
                    <p className="text-[10px] text-white/60 leading-snug">{t('gallery.processingHint')}</p>
                )}

                {/* The bar carries the same number as the ring. A ring reads as
                    "something is happening"; a bar is the thing people actually
                    scan to tell a slow upload from a stalled one. After the
                    bytes are up there is no percentage left to report, so it
                    sweeps instead of pretending to measure the encode. */}
                {!failed && (
                    <div className="w-full max-w-[9rem] h-1.5 rounded-full bg-white/15 overflow-hidden">
                        {job.phase === 'uploading' || job.phase === 'queued' ? (
                            <div
                                className="h-full rounded-full bg-primary-400"
                                style={{
                                    width: `${Math.max(job.progress, 2)}%`,
                                    transition: 'width 0.25s linear',
                                }}
                            />
                        ) : (
                            <div className="h-full w-1/3 rounded-full bg-primary-400 animate-sweep" />
                        )}
                    </div>
                )}

                {failed ? (
                    <button type="button" onClick={onDismiss}
                        className="mt-1 text-[11px] font-bold text-white/80 bg-white/10 hover:bg-white/20 px-3 py-1 rounded-lg transition">
                        {t('gallery.dismiss')}
                    </button>
                ) : job.phase === 'uploading' || job.phase === 'queued' ? (
                    <button type="button" onClick={onCancel}
                        className="mt-1 text-[11px] font-bold text-white/70 hover:text-white transition">
                        {t('gallery.cancel')}
                    </button>
                ) : null}
            </div>

            <p className="absolute bottom-2 left-2 right-2 text-[10px] text-white/50 truncate">{job.name}</p>
        </div>
    );
}

export default function GalleryManager({ items = [], onAdded, onRemoved, onError }) {
    const { t } = useTranslation();
    const [jobs, setJobs] = useState([]);
    const [dragging, setDragging] = useState(false);
    const [confirmId, setConfirmId] = useState(null);
    const [openAt, setOpenAt] = useState(null);
    const fileRef = useRef(null);
    const controllers = useRef({});
    const previews = useRef([]);
    const mounted = useRef(true);

    // `mounted` has to be armed *inside* the effect, not just at its ref
    // initialiser: StrictMode mounts, unmounts and remounts every component in
    // development, so a ref that is only ever set to false by the cleanup stays
    // false for the rest of the component's life. Every `if (mounted.current)`
    // guard below then silently drops its work — the progress never left 0%
    // and the finished upload was never saved, while Cloudflare had the file
    // all along.
    useEffect(() => {
        mounted.current = true;
        // Captured here rather than read in the cleanup: both are mutated in
        // place and never reassigned, so this is the same object either way —
        // it just says so to the linter.
        const running = controllers.current;
        const objectUrls = previews.current;
        return () => {
            mounted.current = false;
            Object.values(running).forEach(c => c.abort());
            objectUrls.forEach(url => URL.revokeObjectURL(url));
        };
    }, []);

    const patch = useCallback((key, values) => {
        setJobs(prev => prev.map(job => (job.key === key ? { ...job, ...values } : job)));
    }, []);

    const drop = useCallback((key) => {
        setJobs(prev => prev.filter(job => {
            if (job.key !== key) return true;
            if (job.preview) URL.revokeObjectURL(job.preview);
            return false;
        }));
        delete controllers.current[key];
    }, []);

    /** One file, start to finish. Runs sequentially — see `enqueue`. */
    const run = useCallback(async (job) => {
        // The controller is minted when the file is queued, not here, so
        // "cancel" works on a file that is still waiting its turn — which is
        // exactly the one someone wants to take back.
        const controller = controllers.current[job.key];
        if (!controller || controller.signal.aborted) { drop(job.key); return; }

        try {
            patch(job.key, { phase: 'uploading', progress: 0 });

            const onProgress = (progress) => {
                if (mounted.current) patch(job.key, { progress });
            };

            const url = job.type === 'video'
                ? await uploadVideo(job.file, { onProgress, signal: controller.signal })
                : await uploadImage(job.file, { onProgress, signal: controller.signal });

            if (!url) throw new Error('handshake');
            if (!mounted.current) return;

            patch(job.key, { phase: 'saving' });
            const res = await api.post('api/update-gallery/', { type: job.type, url });
            const saved = {
                id: res.data?.id || job.key,
                type: res.data?.type || job.type,
                url: res.data?.url || url,
            };

            // Saved is saved: from here the row exists whatever happens next,
            // so a failed wait can never lose the upload.
            if (job.type === 'video') {
                patch(job.key, { phase: 'processing' });
                await waitForStreamReady(saved.url, { signal: controller.signal });
            }

            if (!mounted.current) return;
            onAdded?.(saved);
            drop(job.key);
        } catch (err) {
            if (!mounted.current) return;
            if (err?.name === 'AbortError') { drop(job.key); return; }
            patch(job.key, { phase: 'error', error: t('gallery.failed') });
            onError?.(t('creator.uploadError'));
        } finally {
            delete controllers.current[job.key];
        }
    }, [patch, drop, onAdded, onError, t]);

    const enqueue = useCallback(async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;

        const accepted = [];
        for (const file of files) {
            const isVideo = file.type.startsWith('video/');
            const isImage = file.type.startsWith('image/');

            if (!isVideo && !isImage) { onError?.(t('gallery.unsupported')); continue; }
            if (isImage && file.size > MAX_IMAGE_BYTES) { onError?.(t('gallery.tooLarge')); continue; }
            if (isVideo) {
                const meta = await probeVideo(file);
                if (meta?.duration && meta.duration > MAX_VIDEO_SECONDS + 1) {
                    onError?.(t('gallery.tooLong'));
                    continue;
                }
            }

            const preview = URL.createObjectURL(file);
            previews.current.push(preview);
            const key = nextKey();
            controllers.current[key] = new AbortController();
            accepted.push({
                key,
                file,
                name: file.name,
                type: isVideo ? 'video' : 'photo',
                preview,
                phase: 'queued',
                progress: 0,
            });
        }

        if (accepted.length === 0) return;
        setJobs(prev => [...prev, ...accepted]);

        // One at a time. Three videos racing for the same uplink only makes
        // all three look stuck.
        for (const job of accepted) {
            // eslint-disable-next-line no-await-in-loop
            await run(job);
            if (!mounted.current) return;
        }
    }, [run, onError, t]);

    const pick = (accept) => {
        if (!fileRef.current) return;
        fileRef.current.accept = accept;
        fileRef.current.click();
    };

    const remove = async (item) => {
        try {
            const res = await api.delete(`api/update-gallery/?url=${encodeURIComponent(item.url)}`);
            if (res.status === 200 || res.status === 204) onRemoved?.(item);
        } catch (err) {
            onError?.(t('creator.deleteError'));
        } finally {
            setConfirmId(null);
        }
    };

    const busy = jobs.some(job => job.phase !== 'error');
    // The viewer pages over the same list it is opened from, so the index a
    // tile hands it always lands on that tile.
    const playable = useMemo(() => items.filter(isPlayable), [items]);

    return (
        <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget)) return;
                setDragging(false);
            }}
            onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                enqueue(e.dataTransfer.files);
            }}
            className="relative"
        >
            <input
                ref={fileRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => { enqueue(e.target.files); e.target.value = ''; }}
            />

            {/* Drop zone. A full-width target rather than a tile wedged into the
                grid — dropping onto a 4:5 card is a game of darts. */}
            <div
                onClick={() => pick('image/*,video/*')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') pick('image/*,video/*'); }}
                className={`rounded-2xl border-2 border-dashed px-4 py-5 mb-4 text-center cursor-pointer
                    transition-colors duration-200
                    ${dragging
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-slate-300 bg-white hover:border-primary-400 hover:bg-primary-50/40'}`}
            >
                <div className={`mx-auto w-11 h-11 rounded-full flex items-center justify-center mb-2 transition-colors
                    ${dragging ? 'bg-primary-100 text-primary-600' : 'bg-slate-100 text-slate-400'}`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M4 16v2.5A1.5 1.5 0 005.5 20h13a1.5 1.5 0 001.5-1.5V16" />
                    </svg>
                </div>
                <p className="text-sm font-bold text-slate-700">
                    {dragging ? t('gallery.dropNow') : t('gallery.dropTitle')}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">{t('gallery.dropHint')}</p>

                <div className="flex items-center justify-center gap-2 mt-3">
                    <button type="button"
                        onClick={(e) => { e.stopPropagation(); pick('image/*'); }}
                        className="text-[11px] font-semibold text-slate-600 bg-slate-100 hover:bg-primary-100 hover:text-primary-700 px-3 py-1.5 rounded-lg transition flex items-center gap-1.5">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {t('creator.photo')}
                    </button>
                    <button type="button"
                        onClick={(e) => { e.stopPropagation(); pick('video/*'); }}
                        className="text-[11px] font-semibold text-slate-600 bg-slate-100 hover:bg-primary-100 hover:text-primary-700 px-3 py-1.5 rounded-lg transition flex items-center gap-1.5">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        {t('creator.video')}
                    </button>
                </div>
            </div>

            {items.length === 0 && jobs.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">{t('creator.noMedia')}</p>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                    {jobs.map(job => (
                        <QueueTile
                            key={job.key}
                            job={job}
                            onCancel={() => {
                                controllers.current[job.key]?.abort();
                                // A job that has not started yet has no request
                                // to unwind, so nothing else would clear it.
                                if (job.phase === 'queued') drop(job.key);
                            }}
                            onDismiss={() => drop(job.key)}
                        />
                    ))}

                    {playable.map((item, i) => (
                        <MediaTile
                            key={item.id || item.url}
                            item={item}
                            aspect="portrait"
                            onOpen={() => setOpenAt(i)}
                        >
                            <MediaTypeBadge type={item.type} className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                            {/* Delete asks once. The button sits on a tile people
                                tap to view, and on a phone there is no hover to
                                separate the two intents. */}
                            <div className="absolute top-2 right-2 flex items-center gap-1.5"
                                onClick={(e) => e.stopPropagation()}>
                                {confirmId === (item.id || item.url) ? (
                                    <>
                                        <button type="button" onClick={() => remove(item)}
                                            className="px-2.5 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold transition shadow-lg">
                                            {t('gallery.confirmRemove')}
                                        </button>
                                        <button type="button" onClick={() => setConfirmId(null)}
                                            className="w-7 h-7 rounded-full bg-black/50 backdrop-blur-md text-white flex items-center justify-center transition hover:bg-black/70">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-3.5 h-3.5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </>
                                ) : (
                                    <button type="button"
                                        onClick={() => setConfirmId(item.id || item.url)}
                                        aria-label={t('gallery.remove')}
                                        className="w-8 h-8 rounded-full bg-black/45 backdrop-blur-md ring-1 ring-white/20 text-white
                                            flex items-center justify-center transition hover:bg-red-500
                                            opacity-0 group-hover:opacity-100 focus:opacity-100 max-sm:opacity-100">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </MediaTile>
                    ))}
                </div>
            )}

            {busy && (
                <p className="text-[11px] text-slate-400 text-center mt-3">{t('gallery.keepOpen')}</p>
            )}

            {openAt !== null && (
                <MediaLightbox
                    items={playable}
                    index={openAt}
                    onIndex={setOpenAt}
                    onClose={() => setOpenAt(null)}
                />
            )}
        </div>
    );
}
